import type { ReactElement } from "react";
import { Link } from "react-router";

import type { CenterChargeRow } from "./charge-types.js";
import { adminCenterUrls } from "./urls.js";

/** 契約の欄に要る値（課金の 1 行と停止中の定期課金で共通）。 */
interface TenantOfCharge {
    readonly tenantId: number | null;
    readonly hasTenant: boolean;
    readonly tenantTitle: string | null;
    readonly company: string | null;
    readonly customerId: string | null;
}

/**
 * 契約の欄。結べた行は名前と企業名と契約 1 件の画面へのボタン、
 * <b>結べない行（PAY.JP の顧客がどの契約か分からない）は、その旨と顧客 ID だけ</b>（契約を開かせない）。
 */
export function ChargeTenant({ row }: { readonly row: TenantOfCharge }): ReactElement {
    if (!row.hasTenant || row.tenantId === null) {
        return (
            <>
                {/* 何が分からないのかを書く（PAY.JP の顧客 ID とポータルの契約を突き合わせても当たらなかった） */}
                <span className="block text-xs text-slate-500">どの契約か分かりません</span>
                {row.customerId === null
                    ? null
                    : <span className="block text-xs text-slate-500">{`PAY.JP の顧客 ID: ${row.customerId}`}</span>}
            </>
        );
    }
    return (
        <>
            {row.tenantTitle === null ? null : <span className="block font-medium text-ink-800">{row.tenantTitle}</span>}
            {row.company === null ? null : <span className="block text-xs text-slate-500">{row.company}</span>}
            <Link to={adminCenterUrls.tenant(row.tenantId)} className="btn-secondary btn-sm">{row.tenantId}</Link>
        </>
    );
}

/** PAY.JP の ID（課金・顧客・定期課金。定期課金には今の状態を添える）。 */
function PayjpIds({ row }: { readonly row: CenterChargeRow }): ReactElement {
    return (
        <>
            <span className="block">{row.chargeId}</span>
            {row.customerId === null ? null : <span className="block">{row.customerId}</span>}
            {row.subscriptionId === null ? null : <span className="block">{row.subscriptionId}</span>}
            {row.subscriptionStatusLabel === null ? null : <span className="block">{row.subscriptionStatusLabel}</span>}
        </>
    );
}

/** 課金の行（並びは新しい順にサーバが決める）。 */
export function ChargeRows({ rows }: { readonly rows: readonly CenterChargeRow[] }): ReactElement {
    return (
        <tbody>
            {rows.map((row) => (
                <tr key={row.chargeId}>
                    <td className="whitespace-nowrap text-slate-500">{row.chargedLabel}</td>
                    <td><ChargeTenant row={row} /></td>
                    <td className="whitespace-nowrap">{row.amountLabel}</td>
                    <td>
                        <span className={row.resultClass}>{row.resultLabel}</span>
                        {row.refundLabel === null ? null : <span className="mt-1 block text-xs text-slate-500">{row.refundLabel}</span>}
                    </td>
                    <td className="text-sm">{row.failureLabel}</td>
                    <td className="text-xs text-slate-500"><PayjpIds row={row} /></td>
                </tr>
            ))}
        </tbody>
    );
}
